"use client";

import { Settings, Wrench, Cpu, PlaySquare, MoveDiagonal, HelpCircle } from "lucide-react";
import { useLanguage } from "@/lib/i18n/context";
import { motion } from "framer-motion";
import { useEffect, useMemo } from "react";
import { PageBackground } from "@/components/ui/page-background";
import { pageTheme } from "@/lib/theme/page-theme";

export function ServicesClient() {
  const { dictionary: t } = useLanguage();

  const services = useMemo(() => [
    {
      id: "relokacje",
      name: t.uslugi.list[0].name,
      tag: t.uslugi.list[0].tag,
      description: t.uslugi.list[0].desc,
      bullets: t.uslugi.list[0].bullets,
      icon: MoveDiagonal,
      img: "/media/images/services/wior.webp"
    },
    {
      id: "diagnostyka-elektryczna",
      name: t.uslugi.list[1].name,
      tag: t.uslugi.list[1].tag,
      description: t.uslugi.list[1].desc,
      bullets: t.uslugi.list[1].bullets,
      icon: Cpu,
      img: "/media/images/services/elektryk.webp"
    },
    {
      id: "diagnostyka-mechaniczna",
      name: t.uslugi.list[2].name,
      tag: t.uslugi.list[2].tag,
      description: t.uslugi.list[2].desc,
      bullets: t.uslugi.list[2].bullets,
      icon: Wrench,
      img: "/media/images/services/wrzeciono.webp"
    },
    {
      id: "przeglady-ppm",
      name: t.uslugi.list[3].name,
      tag: t.uslugi.list[3].tag,
      description: t.uslugi.list[3].desc,
      bullets: t.uslugi.list[3].bullets,
      icon: Settings,
      img: "/media/images/services/ppm.webp"
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  useEffect(() => {
    const urls = services.map(s => s.img);
    const preloadImages = () => {
      urls.forEach((url) => {
        const img = new window.Image();
        img.src = url;
      });
    };
    if (document.readyState === "complete") {
      preloadImages();
    } else {
      window.addEventListener("load", preloadImages);
      return () => window.removeEventListener("load", preloadImages);
    }
  }, [services]);

  return (
    <div className={pageTheme.wrapper}>
      <PageBackground />

      {/* Ambient decorative orbs */}
      <div aria-hidden="true" className={pageTheme.orbLeft} />
      <div aria-hidden="true" className={pageTheme.orbRight} />

      <div className="relative z-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

          {/* Page Header */}
          <div className="mx-auto max-w-3xl text-center mb-24">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={pageTheme.pageTag}
            >
              {t.uslugi.tag}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className={pageTheme.pageTitle}
            >
              {t.uslugi.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.16 }}
              className={pageTheme.pageDesc}
            >
              {t.uslugi.desc}
            </motion.p>
          </div>

          {/* Detailed Services Sections */}
          <div className="flex flex-col gap-24 lg:gap-32">
            {services.map((svc, idx) => {
              const isEven = idx % 2 === 0;
              return (
                <motion.div
                  key={svc.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-120px" }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center justify-between`}
                >

                  {/* Text Block */}
                  <div className="w-full lg:w-1/2 flex flex-col items-start text-left space-y-6">
                    <div className={pageTheme.tagBadge}>
                      <svc.icon className="h-4 w-4" />
                      <span>{svc.tag}</span>
                    </div>

                    <h2 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-4xl">
                      {svc.name}
                    </h2>

                    <p className="text-lg leading-relaxed text-zinc-600 dark:text-zinc-400">
                      {svc.description}
                    </p>

                    <ul className="mt-4 space-y-4">
                      {svc.bullets.map((point, pointIdx) => (
                        <li key={pointIdx} className="flex gap-4 items-start">
                          <span className={pageTheme.bulletIcon}>
                            <PlaySquare className="h-3 w-3 fill-current" />
                          </span>
                          <span className="font-medium text-zinc-800 dark:text-zinc-300 leading-relaxed">
                            {point}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <a
                      href="/kontakt"
                      className={`mt-6 inline-flex items-center gap-2 text-base font-semibold ${pageTheme.accentLink} group`}
                    >
                      {t.uslugi.contactBtn}
                      <span aria-hidden="true" className="group-hover:translate-x-1 transition-transform">→</span>
                    </a>
                  </div>

                  {/* Image Block */}
                  <div className="w-full lg:w-1/2">
                    <div className={pageTheme.imageFrame}>
                      <div
                        className="absolute inset-2 md:inset-3 rounded-3xl bg-cover bg-center transition-transform hover:scale-105 duration-700 ease-out"
                        style={{ backgroundImage: `url(${svc.img})` }}
                        role="img"
                        aria-label={svc.name}
                      />
                    </div>
                  </div>

                </motion.div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
